import { useEffect, useState } from "react";
// #1638's ScoreboardFrame.jsx already established the pattern this follows:
// import the raw-src subpath (common-fe/venueTime), never the package root,
// so a public-board route never drags react-oidc-context/CognitoAuthProvider
// into its main chunk. formatVenueTime is also the SAME formatter the
// "as of X venue time" staleness footer already uses (#1637) -- card #1692's
// whole premise is that venue-local time is already available to the page
// through this module, so this reuses it rather than introducing a second
// timezone source.
import { formatVenueTime, isVenueTimezoneConfigured, VENUE_TIME_UNSET_TEXT } from "../utils/venueTime";
import { useVenueTimeZone } from "./VenueTimeZoneContext";
import "./VenueClock.css";

// Card #1692 -- the always-present venue wall clock (bottom-right, every mode
// type/layout family, seconds, once-a-second tick). `venueClockText` is
// exported on its own, separate from the ticking React component below, so
// #1692 AC4's test can pin a fixed instant + timezone and assert the exact
// string this component would render WITHOUT mounting React -- it is the one
// place this component decides what string to show, so pinning it here pins
// the component's real behavior, not a duplicate of it.
//
// #3324 -- the zone is three-state (see VenueTimeZoneContext) and has NO
// default any more: undefined = not loaded -> "" (blank, the component then
// renders an empty clock), null / unusable = unset -> VENUE_TIME_UNSET_TEXT,
// a string = the formatted wall clock. The old silent Pacific default put a
// plausible wrong time on the board for any show with no zone.
export function venueClockText(date, timeZone) {
  if (timeZone === undefined) return "";
  if (!isVenueTimezoneConfigured(timeZone)) return VENUE_TIME_UNSET_TEXT;
  return formatVenueTime(date, { timeZone, seconds: true });
}

// Card #2333 -- the clock renders each character in its own fixed-width cell
// (see VenueClock.css), so the component needs the string's PARTS, not the
// whole string. This splits the ONE string venueClockText produced (it does
// not format a second time), so the text pinned by venueClockText's test is
// still exactly the text on screen. `\s` (not a literal space) because newer
// ICU builds put U+202F NARROW NO-BREAK SPACE before AM/PM.
//
// The tens-of-hours slot is ALWAYS present and is "" for a 1-9 o'clock hour
// (this replaces #2306's U+2007 FIGURE SPACE pad). Returns null for anything
// that is not "H:MM:SS AM|PM" (e.g. "" for an unparseable instant), and the
// component then renders the raw string rather than guessing.
const CLOCK_RE = /^(\d)?(\d):(\d)(\d):(\d)(\d)\s*(AM|PM)$/;
export function venueClockParts(text) {
  const m = CLOCK_RE.exec(text);
  if (!m) return null;
  return {
    hourTens: m[1] ?? "",
    hourOnes: m[2],
    minuteTens: m[3],
    minuteOnes: m[4],
    secondTens: m[5],
    secondOnes: m[6],
    meridiem: m[7],
  };
}

function Digit({ children }) {
  return <span className="venue-clock__cell venue-clock__digit">{children}</span>;
}

function Colon() {
  return <span className="venue-clock__cell venue-clock__colon">:</span>;
}

// Ticks once a second so the seconds digit stays live -- React does not
// re-render on the passage of time by itself (same rationale as
// ScoreboardFrame's useElapsedMs, #1637).
export default function VenueClock() {
  const [now, setNow] = useState(() => new Date());
  // #1788 — the show's zone, from context. This component sits in
  // ScoreboardFrame's bottom-right slot and is passed no props at all, which
  // is exactly why the zone travels by context rather than as a prop.
  const timeZone = useVenueTimeZone();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Plain text in a positioned container -- no button, no link, no
  // tabIndex, no interactive role, no onClick. #1691's no-interactive spec
  // asserts the whole board has zero focusable/interactive elements; this
  // clock must never be the thing that reintroduces one (every child below
  // is a plain <span>).
  //
  // Card #2333 (supersedes #2306, bugs board #209) -- #2306 leaned on the
  // FONT (tabular-nums + a U+2007 pad), which only holds if the active font
  // ships tabular figures and a digit-wide U+2007; the operator can pick any
  // of 8 fonts and the AM/PM still moved on a real board. The fix is LAYOUT:
  // every digit, every colon and the AM/PM label sit in their own
  // fixed-width cell (widths in `ch`, the live font's own "0", so no
  // per-font table; see VenueClock.css), so no tick can change the width of
  // anything. Do NOT "fix" this in formatVenueTime
  // (packages/common-fe/src/utils/venueTime.js) -- shared by other surfaces.
  const text = venueClockText(now, timeZone);
  const parts = venueClockParts(text);

  if (!parts) {
    return (
      <div className="venue-clock" data-testid="venue-clock">
        {/* #3324 -- a not-loaded (blank) or unset clock still needs a line box
            so the pill keeps its height and stays "present" (#1692 AC1, even
            while loading); U+00A0 is that line box and reads as empty. */}
        {text || "\u00a0"}
      </div>
    );
  }

  return (
    <div className="venue-clock" data-testid="venue-clock">
      <Digit>{parts.hourTens}</Digit>
      <Digit>{parts.hourOnes}</Digit>
      <Colon />
      <Digit>{parts.minuteTens}</Digit>
      <Digit>{parts.minuteOnes}</Digit>
      <Colon />
      <Digit>{parts.secondTens}</Digit>
      <Digit>{parts.secondOnes}</Digit>
      {/* A real space keeps the element's text content reading "12:05:03 PM". */}
      <span className="venue-clock__gap"> </span>
      <span className="venue-clock__cell venue-clock__meridiem">
        <span className="venue-clock__meridiem-label">{parts.meridiem}</span>
      </span>
    </div>
  );
}
